import React, { useEffect, useState } from "react";
import { Compass, TrendingUp, Filter, LineChart, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import RodaPosicionamento from "../components/RodaPosicionamento";

const STAGES = [
  { key: "descoberta", label: "Descoberta", desc: "Se apresentando ao mercado" },
  { key: "credibilidade", label: "Credibilidade", desc: "Construindo prova social" },
  { key: "autoridade", label: "Autoridade", desc: "Referência no nicho" },
  { key: "influencia", label: "Influência", desc: "Movimenta pautas" },
];

const AARRR = [
  { key: "acquisition", label: "Aquisição", color: "#FF7A5A" },
  { key: "activation", label: "Ativação", color: "#FF9B5A" },
  { key: "retention", label: "Retenção", color: "#FF4E9E" },
  { key: "revenue", label: "Receita", color: "#C05BFF" },
  { key: "referral", label: "Referência", color: "#8B5CF6" },
];

const MIX_STAGES = [
  { key: "inicio", label: "Início", vals: [60, 30, 10] },
  { key: "crescimento", label: "Crescimento", vals: [40, 40, 20] },
  { key: "estabelecida", label: "Estabelecida", vals: [30, 30, 40] },
];

function computeMomento(pos) {
  const p = pos?.progress?.percent || 0;
  if (p < 25) return "descoberta";
  if (p < 60) return "credibilidade";
  if (p < 85) return "autoridade";
  return "influencia";
}

export default function Frameworks() {
  const { activeBrand } = useAuth();
  const [pos, setPos] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [cal, setCal] = useState(null);

  useEffect(() => {
    if (!activeBrand) return;
    (async () => {
      try {
        const [p, f, c] = await Promise.all([
          api.get(`/positioning/${activeBrand.brand_id}`),
          api.get(`/funnel/${activeBrand.brand_id}`),
          api.get(`/calendar/${activeBrand.brand_id}`),
        ]);
        setPos(p.data); setFunnel(f.data); setCal(c.data);
      } catch { /* noop */ }
    })();
  }, [activeBrand]);

  if (!pos || !funnel || !cal) return <div className="p-10 text-muted font-mono text-xs">carregando…</div>;

  const currentMomento = computeMomento(pos);
  const vol = funnel.vol || {};
  const maxVol = Math.max(...Object.values(vol).map(Number), 1);

  // Mix reals
  const posts = cal.posts || [];
  const metaCount = { Alcance: 0, Autoridade: 0, "Relação": 0 };
  posts.forEach((p) => { if (metaCount[p.meta] !== undefined) metaCount[p.meta] += 1; });
  const total = Math.max(posts.length, 1);
  const mix = {
    Alcance: Math.round((metaCount.Alcance / total) * 100),
    Autoridade: Math.round((metaCount.Autoridade / total) * 100),
    "Relação": Math.round((metaCount["Relação"] / total) * 100),
  };
  const currentStage = MIX_STAGES.find((s) => s.key === cal.stage) || MIX_STAGES[0];

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="frameworks-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// método · visualizações</div>
        <h1 className="text-3xl font-extrabold">Frameworks</h1>
        <p className="text-muted text-sm mt-1">Visualizações somente-leitura alimentadas pelos dados de <span className="grad-text font-semibold">{activeBrand?.name}</span>.</p>
      </div>

      {/* Roda de Posicionamento */}
      <div className="card p-6" data-testid="fw-roda">
        <div className="grid md:grid-cols-[280px_1fr] gap-6 items-center">
          <div style={{ width: 280 }}>
            <RodaPosicionamento steps={pos.steps || {}} size={280} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Compass size={16} className="grad-text" />
              <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// roda de posicionamento</div>
            </div>
            <h2 className="text-xl font-bold">Você × Mercado × Sociedade → Posicionamento</h2>
            <p className="text-sm text-muted mt-1 mb-3">Cada vetor acende conforme o wizard é preenchido. O centro reflete a força da síntese.</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="card-2 p-2"><span className="w-2 h-2 rounded-full inline-block mr-1" style={{ background: "#FF7A5A" }}></span> Você</div>
              <div className="card-2 p-2"><span className="w-2 h-2 rounded-full inline-block mr-1" style={{ background: "#C05BFF" }}></span> Mercado</div>
              <div className="card-2 p-2"><span className="w-2 h-2 rounded-full inline-block mr-1" style={{ background: "#FF4E9E" }}></span> Sociedade</div>
            </div>
          </div>
        </div>
      </div>

      {/* Momento de Marca */}
      <div className="card p-6" data-testid="fw-momento">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="grad-text" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// momento de marca</div>
            <h2 className="text-xl font-bold">Trilha Descoberta → Influência</h2>
          </div>
        </div>
        <div className="relative">
          <div className="absolute top-5 left-4 right-4 h-0.5" style={{ background: "var(--panel-2)" }} />
          <div className="grid grid-cols-4 gap-2 relative">
            {STAGES.map((s, i) => {
              const idx = STAGES.findIndex((x) => x.key === currentMomento);
              const done = i <= idx;
              return (
                <div key={s.key} className="text-center" data-testid={`momento-${s.key}`}>
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center text-sm font-bold relative z-10 ${done ? "grad-bg text-white" : "card-2 text-muted"}`}>
                    {i + 1}
                  </div>
                  <div className={`text-sm mt-2 font-semibold ${i === idx ? "grad-text" : done ? "" : "text-muted"}`}>{s.label}</div>
                  <div className="text-[10px] text-muted mt-0.5">{s.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Funil AARRR */}
      <div className="card p-6" data-testid="fw-aarrr">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={16} className="grad-text" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// funil de maturidade</div>
            <h2 className="text-xl font-bold">AARRR — Aquisição → Referência</h2>
          </div>
        </div>
        <div className="space-y-2">
          {AARRR.map((s, i) => {
            const width = 100 - i * 15;
            return (
              <div key={s.key} className="flex items-center gap-3">
                <div className="w-28 text-sm font-semibold text-right">{s.label}</div>
                <div className="flex-1">
                  <div className="h-8 rounded flex items-center px-3 text-xs text-white font-medium" style={{ width: `${width}%`, background: s.color }}>
                    {s.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mix + Cadeia de Métricas */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-6" data-testid="fw-mix">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="grad-text" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// mix de esforço</div>
              <h2 className="text-lg font-bold">60/30/10 por maturidade</h2>
            </div>
          </div>
          <div className="space-y-3">
            {MIX_STAGES.map((s) => {
              const active = s.key === cal.stage;
              return (
                <div key={s.key} className={`card-2 p-3 ${active ? "ring-2 ring-[#C05BFF]" : ""}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm">{s.label}</div>
                    <div className="text-xs text-muted font-mono">{s.vals.join(" / ")}</div>
                  </div>
                  <div className="flex h-2 rounded-full overflow-hidden">
                    <div style={{ width: `${s.vals[0]}%`, background: "#FF7A5A" }} />
                    <div style={{ width: `${s.vals[1]}%`, background: "#FF4E9E" }} />
                    <div style={{ width: `${s.vals[2]}%`, background: "#C05BFF" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-muted mt-3">
            Seu mix atual (<strong>{currentStage.label}</strong>): {mix.Alcance}/{mix.Autoridade}/{mix["Relação"]}
          </div>
        </div>

        <div className="card p-6" data-testid="fw-chain">
          <div className="flex items-center gap-2 mb-4">
            <LineChart size={16} className="grad-text" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// cadeia de métricas</div>
              <h2 className="text-lg font-bold">Do alcance à venda</h2>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { key: "alcance", label: "Alcance", color: "#FF7A5A" },
              { key: "cliquesBio", label: "Cliques na bio", color: "#FF9B5A" },
              { key: "acessoPagina", label: "Acesso à página", color: "#FF4E9E" },
              { key: "leads", label: "Leads", color: "#D856DE" },
              { key: "mql", label: "MQL", color: "#C05BFF" },
              { key: "vendas", label: "Vendas", color: "#8B5CF6" },
            ].map((s) => {
              const v = Number(vol[s.key] || 0);
              const w = Math.max((v / maxVol) * 100, 4);
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <div className="w-24 text-xs">{s.label}</div>
                  <div className="flex-1 h-3 rounded" style={{ background: "var(--panel-2)" }}>
                    <div className="h-full rounded" style={{ width: `${w}%`, background: s.color }} />
                  </div>
                  <div className="w-16 text-xs text-right font-mono">{v.toLocaleString("pt-BR")}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
